import Catalyst from '@Services/Catalyst';
import { getProjectRoutesTypeFromInfoMapForAA, getProjectDetail, RouteType } from '@Pages/RetroSynthesis/constants';

export const projectRouteViewTypes = Object.freeze({
  tree: 'tree',
  table: 'table',
});

export const getProjectRouteLevelContent = ({ projectId, projectInfo, routeId, routeType }) => [
  {
    id: `project:${projectId}`,
    detail: getProjectDetail({ ...projectInfo, id: projectId }),
    type: getProjectRoutesTypeFromInfoMapForAA(projectInfo),
  },
  {
    id: `project:${projectId}`,
    id2: `route:${routeId}`,
    type: routeType ? RouteType.trackingMap[routeType] : '',
  },
];

export const getStepLevelContent = ({ projectId, routeId, stepNumber }) => ({
  id: `project:${projectId}`,
  id2: `route:${routeId}`,
  id3: `step${stepNumber}`,
  type: 'step',
});

export const onPageLoad = ({ viewType, projectId, routeId, isTransferFromReaction, routeType, projectInfo }) => {
  const data = {
    page: {
      name: `retrosynthesis:route:view:${projectRouteViewTypes[viewType]}`,
    },
    content: [...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo })],
  };
  if (isTransferFromReaction) {
    data.search = {
      criteria: 'transfer-to-retrosynthesis',
    };
  }
  Catalyst.trackEvent('newPage', data);
};

export const onStepAvailableWithDelay = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentView', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    link: {
      name: 'delay-load:step',
      type: 'retrosynthesis',
    },
  });
};

export const onStepSelected = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentView', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    link: {
      name: 'view:step',
      type: 'retrosynthesis',
    },
  });
};

export const onShoppingCartOpened = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentInteraction', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    action: {
      // product name 'rx2' will automatically be prepended to action.name for contentInteraction events by AA lib
      name: 'retrosynthesis:shopping cart',
    },
  });
};

export const onShoppingCartOptionSelected = ({ projectId, routeId, stepNumber, routeType, projectInfo, option }) => {
  Catalyst.trackEvent('productFeatureUsed', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    feature: {
      name: `rx2:synthesisplanner:availability:${option}`,
    },
  });
};

export const onZoom = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentInteraction', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    action: {
      name: 'retrosynthesis:zoom',
    },
  });
};

export const onFitClick = ({ projectId, routeId, routeType, projectInfo }) => {
  Catalyst.trackEvent('buttonClick', {
    buttonType: 'button:fit view', // v124
    content: getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
  });
};

export const onResize = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('buttonClick', {
    buttonType: 'resize-bar:move', // v124
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
  });
};

export const onCopyRoute = ({ projectId, routeId, routeType, projectInfo }) => {
  Catalyst.trackEvent('buttonClick', {
    buttonType: 'button:copy route',
    content: getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
  });
};

export const onHamburgerMenuOpened = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentInteraction', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    action: {
      name: 'retrosynthesis:option',
    },
  });
};

export const onFindSimilarClicked = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentInteraction', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    action: {
      name: 'retrosynthesis:option:find similar',
    },
  });
};

export const onViewRelatedMarkushClicked = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentInteraction', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    action: {
      name: 'retrosynthesis:option:view related markush',
    },
  });
};

export const onOpenInDatabaseClicked = ({ projectId, routeId, stepNumber, routeType, projectInfo, option }) => {
  Catalyst.trackEvent('contentInteraction', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    action: {
      name: `retrosynthesis:option:open in database:${option}`,
    },
  });
};

export const onCopyToQueryClicked = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentInteraction', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    action: {
      name: 'retrosynthesis:option:copy structure to query',
    },
  });
};

export const onSynthesisPlannerClicked = ({ projectId, routeId, stepNumber, routeType, projectInfo }) => {
  Catalyst.trackEvent('contentInteraction', {
    content: [
      ...getProjectRouteLevelContent({ projectId, routeId, routeType, projectInfo }),
      getStepLevelContent({ projectId, routeId, stepNumber }),
    ],
    action: {
      name: 'retrosynthesis:synthesis plan',
    },
  });
};
